import { useQuery } from '@tanstack/react-query';

import type { OrdersServiceSort,OrdersTableRow, PaginatedResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Search orders parameters
 */
export interface SearchOrdersParams {
	query?: string;
	limit?: number;
	page?: number;
	sortBy?: OrdersServiceSort;
	facets?: string[];
	filters?: {
		buyerId?: string;
		programOwnerId?: string;
		paymentStatus?: string | string[];
		paymentChoice?: string[];
		financingStatus?: string | string[];
		tabs?: string[];
		actionRequired?: boolean;
		name?: string | string[];
		vehicleType?: string | string[];
		registrationState?: string;
		orderDateRange?: { gte: number; lte?: number };
		fulfillmentType?: 'delivery' | 'pickup';
		fulfillmentDateRange?: { gte: number; lte?: number };
	};
	timezone?: string;
}

/**
 * API function to search orders with optional filters and pagination
 * @param servicesProviderId - Required services provider ID
 * @param params - Optional search parameters
 */
export const searchOrders = async (
	servicesProviderId: string,
	params: SearchOrdersParams = {},
): Promise<PaginatedResponse<OrdersTableRow>> => {
	const timezone = params.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
	const response = await apiClient.post<PaginatedResponse<OrdersTableRow>>(
		ordersEndpoints.search(servicesProviderId),
		{ ...params, timezone },
	);
	return response.data;
};

/**
 * Hook for searching orders with optional filters and pagination
 */
export const useSearchOrders = (params: SearchOrdersParams = {}) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.list(servicesProviderId, params).queryKey,
		queryFn: () => searchOrders(servicesProviderId, params),
		enabled: Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
